/**
 * Single source of truth for semantic status colors.
 *
 * Any status pill, dot, or label MUST go through one of these tones
 * (`<StatusBadge />`, `<StatusDot />`) instead of raw Tailwind palette
 * classes (green/red/amber/blue/purple). This keeps the product surface
 * calm and consistent across light and dark themes.
 *
 * Tokens resolve to CSS custom properties declared in `src/index.css`:
 *   --color-fin-accent   (primary action / info-accent)
 *   --color-fin-green    (profit / success)
 *   --color-fin-warning  (at risk / pending)
 *   --color-fin-danger   (loss / failure)
 *   --color-fin-info     (informational)
 *   --color-fin-purple   (investment / hybrid)
 */

export type StatusTone = 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'purple';

/** Tailwind class fragments for filled (tinted) badges. */
export const STATUS_TONE_FILL: Record<StatusTone, string> = {
  accent: 'bg-fin-accent/15 text-fin-accent',
  success: 'bg-fin-green/15 text-fin-green',
  warning: 'bg-fin-warning/15 text-fin-warning',
  danger: 'bg-fin-danger/15 text-fin-danger',
  info: 'bg-fin-info/15 text-fin-info',
  muted: 'bg-fin-gray-800/60 text-fin-gray-400',
  purple: 'bg-fin-purple/15 text-fin-purple',
};

/** Tailwind class fragments for outlined (bordered) badges. */
export const STATUS_TONE_OUTLINE: Record<StatusTone, string> = {
  accent: 'border border-fin-accent/30 text-fin-accent',
  success: 'border border-fin-green/30 text-fin-green',
  warning: 'border border-fin-warning/30 text-fin-warning',
  danger: 'border border-fin-danger/30 text-fin-danger',
  info: 'border border-fin-info/30 text-fin-info',
  muted: 'border border-fin-gray-700 text-fin-gray-500',
  purple: 'border border-fin-purple/30 text-fin-purple',
};

/** Solid dot (for status / activity dots beside copy). */
export const STATUS_TONE_DOT: Record<StatusTone, string> = {
  accent: 'bg-fin-accent',
  success: 'bg-fin-green',
  warning: 'bg-fin-warning',
  danger: 'bg-fin-danger',
  info: 'bg-fin-info',
  muted: 'bg-fin-gray-600',
  purple: 'bg-fin-purple',
};
